import React, { useState, useEffect, memo, useMemo, useRef } from "react";
import { DatePicker } from "antd";
import 'antd/dist/reset.css';
import styled, { css, createGlobalStyle } from 'styled-components';
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";

dayjs.extend(weekday);
dayjs.extend(localeData);

const TimePickerStyle = createGlobalStyle`
  .ant-picker-dropdown.aboveModal {
    z-index: 13000 !important;
  }
  .ant-picker-dropdown.aboveModal.ant-slide-up-leave,
  .ant-picker-dropdown.aboveModal.ant-slide-up-leave-active,
  .ant-picker-dropdown.aboveModal.ant-slide-up-enter,
  .ant-picker-dropdown.aboveModal.ant-slide-up-enter-active,
  .ant-picker-dropdown.aboveModal.ant-slide-up-appear,
  .ant-picker-dropdown.aboveModal.ant-slide-up-appear-active {
    animation: none !important;
    transition: none !important;
  }
  .ant-picker-dropdown.custom-dropdown.createDateTime,
  .ant-picker-dropdown.createDateTime {
    .ant-picker-time-panel-column {
      width: 90px !important;
      min-width: 90px !important;
    }
    .ant-picker-time-panel-column > li {
      width: 90px !important;
      min-width: 90px !important;
      padding: 0 24px !important;
      text-align: center !important;
    }
    .ant-picker-time-panel {
      width: auto !important;
    }
    .ant-picker-time-panel-inner {
      width: auto !important;
    }
    .ant-picker-time-panel-column:not(:first-child) {
      border-left: 1px solid #f0f0f0;
    }
  }
`;

const Container = styled.div`
  position: relative;
  margin-top: 16px;
  width: 100%;
`;

const Label = styled.label`
  position: absolute;
  top: -10px;
  left: 10px;
  background: #fff;
  padding: 0 4px;
  font-size: 12px;
  color: #090606b3;
  transition: 0.2s ease all;
  pointer-events: none;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 40px;
  ${({ hasError }) =>
    hasError &&
    css`
      border-color: red;
    `}
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 5px;
`;

const CustomDateTimePicker = ({ 
    value, 
    onChange, 
    minDate, 
    label, 
    disabled, 
    minDateMessage,
    allowPastDates = false,
    customError = "",
    popupAboveModal = false
}) => {
    const [focused, setFocused] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [open, setOpen] = useState(false);
    const savedScrollY = useRef(0);
    const touchPreventRef = useRef(null);

    // Get current date/time
    const now = useMemo(() => dayjs(), []);
    
    // Memoize the dayjs value to prevent unnecessary re-renders
    const dayjsValue = useMemo(() => {
        if (!value) return null;
        
        // Handle different input formats
        if (dayjs.isDayjs(value)) {
            return value;
        }
        
        // Convert string or Date to dayjs
        const dayjsDate = dayjs(value);
        return dayjsDate.isValid() ? dayjsDate : null;
    }, [value]);

    // Determine the effective minimum date
    const effectiveMinDate = useMemo(() => {
        if (allowPastDates && !minDate) return null;
        
        // Use provided minDate or current date/time, whichever is later
        const providedMin = minDate ? (dayjs.isDayjs(minDate) ? minDate : dayjs(minDate)) : null;
        
        if (!allowPastDates && !providedMin) {
            return now;
        }
        
        if (!allowPastDates && providedMin) {
            return providedMin.isAfter(now) ? providedMin : now;
        }
        
        return providedMin;
    }, [minDate, now, allowPastDates]);

    // Function to update error state and notify parent
    const updateErrorState = (errorState, errorMsg = "") => {
        setHasError(errorState);
    };

    function forceNoScroll() {
        savedScrollY.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
        
        document.documentElement.classList.add('fullscreen-no-scroll');
        document.body.classList.add('fullscreen-no-scroll');
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollY.current}px`;
        document.body.style.width = '100%';
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.overflowX = 'hidden';
        document.documentElement.style.overflowY = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.overflowX = 'hidden';
        document.body.style.overflowY = 'hidden';
        
        const preventTouchMove = (e) => {
            const target = e.target;
            if (!target || typeof target.closest !== 'function') {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
            const pickerDropdown = target.closest('.ant-picker-dropdown');
            const pickerPanel = target.closest('.ant-picker-panel');
            const pickerInput = target.closest('.ant-picker');
            const pickerTimePanel = target.closest('.ant-picker-time-panel');
            
            if (
                pickerDropdown === null &&
                pickerPanel === null &&
                pickerInput === null &&
                pickerTimePanel === null
            ) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };
        
        touchPreventRef.current = preventTouchMove;
        document.addEventListener('touchmove', preventTouchMove, { passive: false, capture: true });
        document.addEventListener('wheel', preventTouchMove, { passive: false, capture: true });
    }
      
    function removeNoScroll() {
        if (touchPreventRef.current) {
            document.removeEventListener('touchmove', touchPreventRef.current, { capture: true });
            document.removeEventListener('wheel', touchPreventRef.current, { capture: true });
            touchPreventRef.current = null;
        }
        
        document.documentElement.classList.remove('fullscreen-no-scroll');
        document.body.classList.remove('fullscreen-no-scroll');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.documentElement.style.overflow = '';
        document.documentElement.style.overflowX = '';
        document.documentElement.style.overflowY = '';
        document.body.style.overflow = '';
        document.body.style.overflowX = '';
        document.body.style.overflowY = '';
        
        const scrollY = savedScrollY.current || 0;
        requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);
        });
    }

    const handleOpenChange = (isOpen) => {
        setOpen(isOpen);
        if (isOpen) {
            forceNoScroll();
        } else {
            removeNoScroll();
        }
    };

    const handleFocus = () => {
        setFocused(true);
    };
    
    const handleBlur = () => {
        if (!dayjsValue) {
            setFocused(false);
        }
    };
    
    const handleChange = (date) => {
        if (onChange) {
            // Always pass dayjs object to maintain consistency
            onChange(date);
        }
        
        if (date) {
            const currentTime = dayjs();
            if (date.isBefore(currentTime, 'minute')) {
                updateErrorState(true, "Selected date/time has passed. Please select a current or future date/time.");
            } else {
                updateErrorState(false);
            }
        } else {
            updateErrorState(false);
        }
    };

    // Memoize disabledDate function to prevent recreation on each render
    const disabledDate = useMemo(() => {
        if (!effectiveMinDate) return null;
        
        return (current) => {
            return current && current.isBefore(effectiveMinDate, 'day');
        };
    }, [effectiveMinDate]);

    // Memoize disabledTime function for same-day time restrictions
    const disabledTime = useMemo(() => {
        if (!effectiveMinDate) return null;
        
        return (current) => {
            if (!current) return {};
            
            // Only apply time restrictions if it's the same day as the minimum date
            if (!current.isSame(effectiveMinDate, 'day')) {
                return {};
            }
            
            const minHour = effectiveMinDate.hour();
            const minMinute = effectiveMinDate.minute();
            const currentHour = current.hour();
            
            return {
                disabledHours: () => {
                    const hours = [];
                    for (let i = 0; i < minHour; i++) {
                        hours.push(i);
                    }
                    return hours;
                },
                disabledMinutes: (selectedHour) => {
                    if (selectedHour === minHour) {
                        const minutes = [];
                        for (let i = 0; i < minMinute; i++) {
                            minutes.push(i);
                        }
                        return minutes;
                    }
                    return [];
                },
                disabledSeconds: () => [] // You can add second restrictions if needed
            };
        };
    }, [effectiveMinDate]);

    // Check for error when value changes or periodically against current time
    // useEffect(() => {
    //     if (!dayjsValue) {
    //         updateErrorState(false);
    //         return;
    //     }

    //     const validateDateTime = () => {
    //         const currentTime = dayjs();
    //         let errorMsg = "";
            
    //         // Check against current time (always updated)
    //         if (dayjsValue.isBefore(currentTime, "minute")) {
    //             errorMsg = "Selected date/time has passed. Please select a current or future date/time.";
    //             updateErrorState(true, errorMsg);
    //             return;
    //         }
            
    //         // Check against provided minDate if exists
    //         if (effectiveMinDate && dayjsValue.isBefore(effectiveMinDate, "minute")) {
    //             const isCurrentTime = effectiveMinDate.isSame(currentTime, 'minute');
    //             errorMsg = isCurrentTime 
    //                 ? "Date and time must be current or future"
    //                 : `Date must be after ${effectiveMinDate.format("MM-DD-YYYY h:mm A")}`;
    //             updateErrorState(true, errorMsg);
    //             return;
    //         }
            
    //         updateErrorState(false);
    //     };

    //     // Initial validation
    //     validateDateTime();
        
    //     // Set up interval to check against current time every minute
    //     const interval = setInterval(validateDateTime, 60000); // Check every minute
        
    //     return () => clearInterval(interval);
    // }, [dayjsValue, effectiveMinDate, onError]);

    // Memoize error message and sync with error state
    const errorMessage = useMemo(() => {
        if (!dayjsValue) {
            // No value selected - no error
            if (hasError) {
                updateErrorState(false);
            }
            return "";
        }
        

        let errorMsg = "";
        let shouldHaveError = false;
        
        //const currentTime = dayjs();
        // // Check against current time (always updated)
        // if (dayjsValue.isBefore(currentTime, "minute")) {
        //     errorMsg = "Selected date/time has passed. Please select a current or future date/time.";
        //     shouldHaveError = true;
        // }
        // // Check against provided minDate if exists
        // else if (effectiveMinDate && dayjsValue.isBefore(effectiveMinDate, "minute")) {
        //     const isCurrentTime = effectiveMinDate.isSame(currentTime, 'minute');
        //     errorMsg = isCurrentTime 
        //         ? "Date and time must be current or future"
        //         : `Date must be after ${effectiveMinDate.format("MM-DD-YYYY h:mm A")}`;
        //     shouldHaveError = true;
        // }
        
        if (customError) {
            shouldHaveError = true;
            errorMsg = minDateMessage;
        }
        // if (minDateMessage) {
        //     shouldHaveError = true;
        //     errorMsg = minDateMessage;
        // }
        
        // Update error state if it doesn't match
        if (shouldHaveError !== hasError) {
            updateErrorState(shouldHaveError, errorMsg);
        }
        
        return shouldHaveError ? errorMsg : "";
    }, [minDateMessage, effectiveMinDate, hasError, dayjsValue, updateErrorState]);

    useEffect(() => {
        return () => {
            if (open) {
                removeNoScroll();
            }
        };
    }, [open]);

    return (
        <>
            <TimePickerStyle />
            <Container>
                <StyledDatePicker
                showTime={{ 
                    use12Hours: true, 
                    format: 'h:mm A',
                }}
                format="MM-DD-YYYY h:mm A"
                value={dayjsValue}
                onChange={handleChange}
                open={open}
                onOpenChange={handleOpenChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabledDate={disabledDate}
                disabledTime={disabledTime}
                disabled={disabled}
                hasLabel={!!label}
                getPopupContainer={popupAboveModal ? () => document.body : (trigger) => trigger.parentElement}
                classNames={{ popup: `custom-dropdown createDateTime${popupAboveModal ? ' aboveModal' : ''}` }}
                transitionName={popupAboveModal ? '' : undefined}
                hasError={hasError}
                allowClear={false}
            />
                <Label focused={focused || !!dayjsValue ? "true" : "false"}>{label}</Label>
                {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </Container>
        </>
    );
};

// More specific memo comparison to prevent unnecessary re-renders
export default memo(CustomDateTimePicker, (prevProps, nextProps) => {
    // Custom comparison for value prop
    const prevValue = prevProps.value;
    const nextValue = nextProps.value;
    
    // Compare dayjs values
    const prevDayjs = prevValue ? dayjs(prevValue) : null;
    const nextDayjs = nextValue ? dayjs(nextValue) : null;
    
    const valueEqual = (prevDayjs && nextDayjs) ? 
        prevDayjs.isSame(nextDayjs) : 
        prevDayjs === nextDayjs;
    
    // Compare minDate
    const prevMinDate = prevProps.minDate ? dayjs(prevProps.minDate) : null;
    const nextMinDate = nextProps.minDate ? dayjs(nextProps.minDate) : null;
    
    const minDateEqual = (prevMinDate && nextMinDate) ? 
        prevMinDate.isSame(nextMinDate) : 
        prevMinDate === nextMinDate;
    
    return (
        valueEqual &&
        minDateEqual &&
        prevProps.label === nextProps.label &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.minDateMessage === nextProps.minDateMessage &&
        prevProps.allowPastDates === nextProps.allowPastDates &&
        prevProps.onChange === nextProps.onChange &&
        prevProps.customError === nextProps.customError &&
        prevProps.popupAboveModal === nextProps.popupAboveModal
    );
});